<template>
	<header class="z-10 py-4 bg-white shadow-sm">
		<div class="flex items-center justify-between h-full px-6 mx-auto">
			<button
				class="p-1 mr-5 -ml-1 rounded-md md:hidden focus:outline-none focus:ring-2 focus:ring-orange-5"
				aria-label="Menu"
				@click="toggleSidebar"
			>
				<svg
					class="w-6 h-6"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M4 6h16M4 12h16M4 18h16"
					/>
				</svg>
			</button>
			<div class="flex justify-end flex-1">
				<ul class="flex items-center flex-shrink-0 space-x-6">
					<li class="relative">
						<Menu
							as="div"
							class="relative"
						>
							<MenuButton class="flex items-center space-x-2 focus:outline-none">
								<div
									v-if="!user?.picture"
									class="w-8 h-8 rounded-full bg-orange-2 flex items-center justify-center"
								>
									<svg class="w-5 h-5 text-orange-7" fill="currentColor" viewBox="0 0 20 20">
										<path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
									</svg>
								</div>
								<img
									v-else
									class="w-8 h-8 rounded-full object-cover"
									:src="user.picture"
									alt="User avatar"
								>
								<span class="text-sm font-medium text-gray-700">
									{{ user?.name || 'User' }}
								</span>
								<svg
									class="w-4 h-4 text-gray-400"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fill-rule="evenodd"
										d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
										clip-rule="evenodd"
									/>
								</svg>
							</MenuButton>
							<transition
								enter-active-class="transition ease-out duration-100"
								enter-from-class="transform opacity-0 scale-95"
								enter-to-class="transform opacity-100 scale-100"
								leave-active-class="transition ease-in duration-75"
								leave-from-class="transform opacity-100 scale-100"
								leave-to-class="transform opacity-0 scale-95"
							>
								<MenuItems class="absolute right-0 w-56 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									<div class="p-3 text-center bg-orange-7 rounded-t-md">
										<div
											v-if="!user?.picture"
											class="w-12 h-12 mx-auto rounded-full bg-orange-2 flex items-center justify-center border-2 border-white"
										>
											<svg class="w-7 h-7 text-orange-7" fill="currentColor" viewBox="0 0 20 20">
												<path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
											</svg>
										</div>
										<img
											v-else
											class="w-12 h-12 mx-auto rounded-full object-cover border-2 border-white"
											:src="user.picture"
											alt="User avatar"
										>
										<p class="mt-2 text-sm font-medium text-white">
											{{ user?.name || 'User' }}
										</p>
									</div>
									<div class="py-1">
										<MenuItem v-slot="{ active }">
											<button
												class="flex items-center w-full px-4 py-2 text-sm text-left"
												:class="active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'"
												@click="logoutUser"
											>
												<svg
													class="w-4 h-4 mr-3"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
													/>
												</svg>
												Log out
											</button>
										</MenuItem>
									</div>
								</MenuItems>
							</transition>
						</Menu>
					</li>
				</ul>
			</div>
		</div>
	</header>
</template>

<script setup>
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';

const router = useRouter();
const { logout, user } = useAuth0();

const emit = defineEmits(['toggle-sidebar']);

function toggleSidebar() {
	emit('toggle-sidebar');
}

function logoutUser() {
	logout();
	router.push({ path: '/' });
}
</script>

<style scoped>
/* Complete Bootstrap override for Windmill header */
header {
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
}

header ul {
	list-style: none !important;
	padding-left: 0 !important;
	margin-bottom: 0 !important;
	margin-top: 0 !important;
}

header li {
	background: transparent !important;
	border: none !important;
	box-shadow: none !important;
	outline: none !important;
	margin: 0 !important;
	padding: 0 !important;
}

header li::before {
	display: none !important;
	content: none !important;
}

/* Button reset - only neutralize Bootstrap defaults, preserve Tailwind utilities */
header button {
	border: none !important;
	box-shadow: none !important;
	text-decoration: none !important;
	-webkit-appearance: none !important;
	appearance: none !important;
}

header button:focus {
	outline: none !important;
	box-shadow: none !important;
}

/* Accessibility: restore focus visibility with custom ring */
header button:focus-visible {
	outline: 2px solid #9333ea !important;
	outline-offset: 2px !important;
	border-radius: 0.25rem !important;
}

/* Image reset */
header img {
	border: none !important;
}

/* Menu items dropdown - override Bootstrap dropdown styles */
header [role="menu"] {
	border: none !important;
	padding: 0 !important;
}

header [role="menuitem"] {
	border: none !important;
	box-shadow: none !important;
	text-decoration: none !important;
}

header [role="menuitem"]:focus-visible {
	outline: 2px solid #9333ea !important;
	outline-offset: -2px !important;
}
</style>
